import Link from 'next/link';
import cn from 'classnames';
import type { CSSProperties } from 'react';
// Types
import { GetArticleBySlug_getArticleBySlug } from 'api/articles/types/GetArticleBySlug';
// Helpers
import {
  addHeadingAnchors,
  estimateReadingMinutes,
} from 'helpers/articleHeadings';
import { getArticleImageAlt, getArticleMentionLinks } from 'helpers/articleSeo';
import { linkFirstMentions } from 'helpers/linkFirstMentions';
import { parseForInnerHTML } from 'helpers/textEditor';
// Layout
import Main from 'layouts/Main/Main';
// UI
import Image from 'ui3/Image/Image';
import HashTags from 'ui3/HashTags/HashTags';
// Components
import TopSection from 'components/common2/TopSection/TopSection';
import MentionsView from 'components/MentionsView/MentionsView';
import SocialShareQRButton from 'components/common3/SocialShareQRButton/SocialShareQRButton';
import ArticleByline from './ArticleByline/ArticleByline';
import ArticleContents from './ArticleContents/ArticleContents';
import ArticleTakeaways from './ArticleTakeaways/ArticleTakeaways';
import ArticleWatchInterview from './ArticleWatchInterview/ArticleWatchInterview';
import ArticleFaq from './ArticleFaq/ArticleFaq';
import ArticleParticipants from './ArticleParticipants/ArticleParticipants';
import ArticleMoreFrom from './ArticleMoreFrom/ArticleMoreFrom';
// Styles
import styles from './News.module.scss';

type NewsProps = {
  storeSlug: string;
  news: GetArticleBySlug_getArticleBySlug | undefined;
};

const News = ({ storeSlug, news }: NewsProps) => {
  const newsSlug = news?.slug;

  const storeLink = `/${storeSlug}/news`;
  const shareableLink = `/${storeSlug}/news/${newsSlug}`;
  const storeName = news?.store?.storeDetails?.storeName || storeSlug;

  const articleUrl = news?.articleUrl;

  const CoverWrapper = articleUrl ? 'a' : 'div';

  const isInterview = !!news?.sourceStream;
  const isGenerated = news?.__typename === 'GeneratedArticle';
  const coverUrl = news?.url || '';
  // On an interview the two participants get their own section; any other mention stays listed.
  const participantSlugs = isInterview
    ? [
        news?.store?.slug,
        news?.sourceInterviewee?.slug,
        news?.sourceInterviewer?.slug,
      ].filter(Boolean)
    : [];
  const otherMentions = (news?.mentions || []).filter(
    (mention) =>
      !mention.store?.slug || !participantSlugs.includes(mention.store.slug)
  );

  const linkedContent = linkFirstMentions(
    parseForInnerHTML(news?.content || ''),
    getArticleMentionLinks(news)
  );
  // Only generated articles have a predictable outline; legacy content keeps its own markup.
  const { html: content, headings } = isGenerated
    ? addHeadingAnchors(linkedContent)
    : { html: linkedContent, headings: [] };

  const author = news?.store?.slug
    ? {
        slug: news.store.slug,
        name: storeName,
        avatarURL: news.store.storeDetails?.avatarURL,
      }
    : null;

  return (
    <>
      <Main>
        <TopSection className={styles.root}>
          <article className={styles.article}>
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              <ol className={styles.breadcrumbList}>
                <li>
                  <Link href="/" prefetch={false}>
                    <a className={styles.breadcrumbLink}>MILLIONS</a>
                  </Link>
                </li>
                <li>
                  <Link href={storeLink} prefetch={false}>
                    <a className={styles.breadcrumbLink}>
                      {storeName} articles
                    </a>
                  </Link>
                </li>
              </ol>
            </nav>
            {/* A real anchor, so the cover and its caption are both clickable and crawlable. */}
            <CoverWrapper
              {...(articleUrl
                ? { href: articleUrl, target: '_blank', rel: 'noopener' }
                : {})}
              className={cn(styles.imageWrapper, {
                [styles.generatedImageWrapper]: isGenerated,
              })}
              style={
                isGenerated && coverUrl
                  ? ({ '--cover-url': `url("${coverUrl}")` } as CSSProperties)
                  : undefined
              }
            >
              <Image
                className={cn(styles.image, {
                  [styles.imageLink]: !!articleUrl,
                  [styles.generatedImage]: isGenerated,
                })}
                src={coverUrl}
                alt={getArticleImageAlt(news)}
                ssrFill={isGenerated}
              />
              {news?.imageLabel && (
                <span className={styles.imageName}>{news.imageLabel}</span>
              )}
            </CoverWrapper>
            <div className={styles.column}>
              <div className={styles.tagsWrapper}>
                <HashTags
                  tags={news?.hashtags || []}
                  className={styles.tagsWrapper}
                  hashTagClassName={styles.tag}
                />
              </div>
              <h1 className={styles.title}>{news?.title}</h1>

              <div className={styles.shareSegment}>
                <ArticleByline
                  author={author}
                  publishedAt={news?.createdAt}
                  readingMinutes={estimateReadingMinutes(content)}
                />
                <div>
                  <SocialShareQRButton
                    itemTitle={news?.title}
                    modalTitle="Share Article"
                    modalText="Share this article in any way convenient for you"
                    path={shareableLink}
                    twitterText="News alert! Check out my latest drop on @millionsdotco! #MILLIONSco"
                    iconV2
                    isArticleButton
                    smallButton
                  />
                </div>
              </div>
              <ArticleTakeaways takeaways={news?.keyTakeaways || []} />
              <ArticleContents headings={headings} />
              <div
                className={cn(styles.content, {
                  [styles.generatedContent]: isGenerated,
                })}
                dangerouslySetInnerHTML={{ __html: content }}
              ></div>
              {!!otherMentions.length && (
                <MentionsView mentions={otherMentions} />
              )}
              {news?.sourceStream && (
                <ArticleWatchInterview
                  stream={news.sourceStream}
                  fallbackImageUrl={coverUrl}
                />
              )}
              <ArticleFaq items={news?.faq || []} />
              <ArticleParticipants
                store={news?.store || null}
                interviewer={news?.sourceInterviewer || null}
                interviewee={news?.sourceInterviewee || null}
                isInterview={isInterview}
              />
              {news?.id && (
                <ArticleMoreFrom
                  storeSlug={storeSlug}
                  storeName={storeName}
                  currentArticleId={news.id}
                />
              )}
            </div>
          </article>
        </TopSection>
      </Main>
    </>
  );
};

export default News;
